import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { decimalPriceOdds } from '~shared/utils/selections';
import { useOddsFormat } from '~SpectateStore/hooks/userHooks';
import {
    getPriceOdds,
} from '~shared/components/sport-event/utils.js';
import { getI18nBetErrorKey } from '~Betslip/utils/errorMappings';
import formatCurrency from '~shared/utils/currencyUtils';
import {
    calculatePotentialReturnsMultiplier,
} from '~Betslip/utils/payoutCalculators';

const BetDetails = ({ bet }) => {
    const [ t ] = useTranslation();

    const {
        id,
        bet_type,
        decimal_price,
        fraction_price,
        american_price,
        selections,
        selection_name,
        selection_names,
        boosted_odds,
        sport,
        bog_singles,
        bog_multiples,
        success,
        total_stake,
    } = bet;

    const castMarkets = ['forecast', 'tricast'];
    const betType = bet_type.toLowerCase();
    const isCastMarket = castMarkets.includes(betType);
    const isOddsBoost = sport === 'odds-boost';
    const isBogSelection = bog_singles || bog_multiples;
    const betTypesAllowed = ['singles', 'scorecast', ...castMarkets];
    const isMulti = !(betTypesAllowed.includes(betType));
    const oddsFormat = useOddsFormat();

    const singleOdds = () => {
        if (!decimal_price || Number(decimal_price) === 0) {
            return t('betslip.sp');
        }

        switch (oddsFormat) {
            case 'AMERICAN':
                return american_price;
            case 'FRACTION':
                return fraction_price;
            default:
                return decimalPriceOdds(decimal_price);
        }
    };

    const multiOdds = () => {
        const potentialReturnsMultiplier = calculatePotentialReturnsMultiplier(selections);

        if (Number(potentialReturnsMultiplier) !== 0) {
            return getPriceOdds(potentialReturnsMultiplier, oddsFormat, 2);
        }

        return t('betslip.sp');
    };

    const renderBetTypeOdds = () => {
        return (
            <div className='baby-receipt__bets-section-header'>
                <div>
                    <span>
                        {
                            isMulti
                                ? t(`bet_types.name.${bet_type}`)
                                : t('betslip.single')
                        }
                    </span>
                    {' '}
                    <span className="baby-receipt__bets-section-header-odds" dir="auto">
                        {
                            isMulti
                                ? multiOdds()
                                : singleOdds()
                        }
                        {
                            boosted_odds
                                ? <span className='baby-receipt__bets-section-header-boosted-odds'></span>
                                : null
                        }
                        {
                            isOddsBoost
                                ? <span className='baby-receipt__bets-section-header-odds-boost'>
                                    <span className='baby-receipt__bets-section-header-odds-boost-icon'></span>
                                </span>
                                : null
                        }
                        {
                            isBogSelection
                                ? <span className='baby-receipt__bets-section-header-bog'></span>
                                : null
                        }
                    </span>
                </div>
                <span>
                    {formatCurrency(total_stake)}
                </span>
            </div>
        );
    };

    const multiSelectionNames = () => {
        const selectionNames = selections.map((selection, index) => {
            let selectionName = selection.selection_name;

            if (selections.length - index > 1) {
                selectionName = `${selectionName}, `;
            }

            return selectionName;
        });

        return selectionNames;
    };

    const singleSelectionName = () => {
        if (isCastMarket) {
            return selection_names.map((name, index) => (
                <span key={index}>{name}
                    <span className="babyslip__selection-hint">
                        {' '}
                        {t(`betslip.fctc_header_${index + 1}`)}
                    </span>
                    {index < selection_names.length - 1 ? ', ' : ''}
                </span>
            ));
        }

        return selection_name;
    };

    const renderSelectionName = () => {
        const selectionName = isMulti
            ? multiSelectionNames()
            : singleSelectionName();

        return (
            <div className="baby-receipt__bets-section-selection-name">
                {selectionName}
            </div>
        );
    };

    const renderBetErrorMsg = () => {
        const {
            id,
            info,
        } = bet.error;

        const {
            minimum_odds_allowed: minOdds,
            stake_allowed: stakeAllowed,
        } = info || {};

        const param = minOdds || formatCurrency(stakeAllowed);

        const msg = t(`betslip.${getI18nBetErrorKey(id)}`, { param });

        return (
            <p className="baby-receipt__bets-section-error-msg">{msg}</p>
        );
    };

    return (
        <div className='baby-receipt__bets-section-container'>
            {
                renderBetTypeOdds()
            }
            {
                renderSelectionName()
            }
            {
                success
                ? <div className='baby-receipt__bets-section-betId'>
                    <span>{t('betslip.bet_id')} </span>
                    <span>{id}</span>
                </div>
                : renderBetErrorMsg()
            }
        </div>
    );
};

BetDetails.propTypes = {
    bet: PropTypes.object,
};

export default BetDetails;
